import log from 'loglevel';
import dashjs from 'dashjs';
import { Html5 } from '../html5/Html5';
import ProviderInterface from '../../ProviderInterface';
import {
  InitOptions,
  State,
  Source,
  QualityLevel,
  QualityLevelKind,
  AudioTrack,
  VideoTrack,
  TextTrack,
  Provider,
  TextTrackKind
} from '../../../types/player';
import { QualityLevelEvents } from '../../PlayerEvents';
import { ErrorCode, Error, ErrorMessage } from '../../../types/error';
import { PlayerEvents } from '../../PlayerEvents';
import { Options } from '../../optionsManager/Options';
import { doc } from 'prettier';

class Dash extends Html5 implements ProviderInterface {
  dashJS: dashjs.MediaPlayerClass;
  firstLoaded = false;
  loadRetryer: ReturnType<typeof setTimeout> = null as any;
  lastPlayPosition = -1;
  dvrWindow = 0;
  isDashSubtitlesEnabled = false;
  streamInitialised = false;

  constructor(element: HTMLElement, options: Options, events: PlayerEvents) {
    super(element, options, events);
    this.dashJS = dashjs.MediaPlayer().create();
    //@ts-ignore
    window.dashJS = this.dashJS;
    log.debug('Dash init', this.dashJS);
  }

  onManifestLoaded(event: any): void {
    if(this.streamInitialised) 
      return;      
    this.streamInitialised = true;
    log.debug('Dash onManifestLoaded');

    const availableQualityLevels = this.dashJS.getBitrateInfoListFor('video');

    for (let i = 0; i < availableQualityLevels.length; i++) {
      const qualityLevel = availableQualityLevels[i];
      this.qualityLevels[QualityLevelKind.VIDEO].push({
        bitrate: qualityLevel.bitrate,
        height: qualityLevel.height || 0,
        width: qualityLevel.width || 0,
        index: qualityLevel.qualityIndex,
        data: qualityLevel as any,
      });
    }

    this.qualityLevelCurrentIndex[QualityLevelKind.VIDEO] = this.dashJS.getQualityFor('video');

    const availableQualityLevelsAudio =
      this.dashJS.getBitrateInfoListFor('audio');

    for (let i = 0; i < availableQualityLevelsAudio.length; i++) {
      const qualityLevelAudio = availableQualityLevelsAudio[i];
      this.qualityLevels[QualityLevelKind.AUDIO].push({
        bitrate: qualityLevelAudio.bitrate,
        index: qualityLevelAudio.qualityIndex,
        data: qualityLevelAudio as any,
      });
    }

    this.qualityLevelCurrentIndex[QualityLevelKind.AUDIO] = this.dashJS.getQualityFor('audio');

    const availableAudioTracks = this.dashJS.getTracksFor('audio');
    for (let i = 0; i < availableAudioTracks.length; i++) {
      const audioTrack = availableAudioTracks[i];
      this.audioTracks.push({
        index: audioTrack.index as number,
        label: audioTrack.lang as string,
        data: audioTrack as any,
      });
    }

    try {
      this.currentAudioTrack =
        (this.dashJS.getCurrentTrackFor('audio')?.index as number) || -1;
    } catch (err) {
      log.debug(err);
    }

    let availableVideoTracks = this.dashJS.getTracksFor('video');
    for (let i = 0; i < availableVideoTracks.length; i++) {
      const videoTrack = availableVideoTracks[i];
      let role = '';
      if(videoTrack.roles !== null)
          role = videoTrack.roles.join(', ');
      this.videoTracks.push({
        index: videoTrack.index as number,
        label: `${videoTrack.lang} - ${role}`,
        data: videoTrack as any,
      });
    }

    try {
      this.currentVideoTrack =
        (this.dashJS.getCurrentTrackFor('video')?.index as number) || -1;
    } catch (err) {
      log.debug(err);
    }

    const availableTextTracks = this.dashJS.getTracksFor('text');    
    if(availableTextTracks.length > 0) {
      this.isDashSubtitlesEnabled = true;
      if(this.textTracks.length > 0) {
        this.textTracks = [];
        console.warn('be careful, subtitles are already set, it will be reseted because of native hls subtitles');
      }       
    }
    for (let i = 0; i < availableTextTracks.length; i++) {
      const textTrackTrack = availableTextTracks[i];
      let role = '';
      if(textTrackTrack.roles !== null)
          role = textTrackTrack.roles[0];

      this.textTracks.push({
        index: textTrackTrack.index as number,
        label: textTrackTrack.lang as string,
        data: textTrackTrack as any,
        src: '',
        srclang: '',
        kind: role === 'caption' ? TextTrackKind.CAPTIONS : TextTrackKind.SUBTITLES,
      });
    }

    this.currentTextTrack = this.dashJS.getCurrentTextTrackIndex();
  }

  onQualityChanged(event: any): void {
    log.debug('Dash onQualityChanged', event);
    let kind = QualityLevelKind.VIDEO;
    if (event.mediaType === 'audio')
      kind = QualityLevelKind.AUDIO;

    this.qualityLevelCurrentIndex[kind] = event.newQuality;

    this._events.qualityLevelChanged(
      this.getCurrentQualityLevel(kind),
      this.qualityLevelCurrentIndex[kind],
      this.isAutoQualityLevel(),
      kind,
    );
  }

  onTrackChanged(event: any): void {
    log.debug('Dash onTrackChanged', event);
    /*if (event.mediaType === 'audio') {
            this.setCurrentAudioTrack(event.newTrack.index);
        } else if (event.mediaType === 'text') {
            this.setCurrentTextTrack(event.newTrack.index);
        }*/
  }

  onDashJsError(event: any): void | null {
    log.debug('Dash onError', event);
    if (!event || !event.error) {
      return null;
    }

    let errorCode: ErrorCode = ErrorCode.UNKNOWN_ERROR;

    switch (event.error.code) {
      case dashjs.MediaPlayer.errors.MANIFEST_LOADER_PARSING_FAILURE_ERROR_CODE:
      case dashjs.MediaPlayer.errors.MANIFEST_LOADER_LOADING_FAILURE_ERROR_CODE:
        errorCode = ErrorCode.PLAYER_BAD_REQUEST_ERROR;
        break;
      case dashjs.MediaPlayer.errors.FRAGMENT_LOADER_LOADING_FAILURE_ERROR_CODE:
        errorCode = ErrorCode.PLAYER_AUTH_FAILED_ERROR;
        break;
      /* case dashjs.MediaPlayer.errors.BUFFER_NOSTREAM_ERROR_CODE:
                 errorCode = ErrorCode.MEDIA_ERROR;
                 break;*/
      default:
        break;
    }

    this._events.error({
      code: errorCode,
      message: ErrorMessage.codes[errorCode].message,
    });
  }

  init() {
    this.dashJS.initialize(
      this.videoElement,
      this.options.getSource().url,
      true,
    );

    //if there is TTML subtitles, we need to create a div for them
    let parent = this.videoElement.parentElement as HTMLElement;
    let div = document.createElement('div');    
    parent.appendChild(div);
    this.dashJS.attachTTMLRenderingDiv(div);
    
    this.dashJS.updateSettings(this.options.getProviderNativeOptions(Provider.Dash));

    this.dashJS.on(
      dashjs.MediaPlayer.events.STREAM_INITIALIZED,
      this.onManifestLoaded.bind(this),
    );

    this.dashJS.on(
      dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED,
      this.onQualityChanged.bind(this),
    );

    this.dashJS.on(
      dashjs.MediaPlayer.events.TRACK_CHANGE_RENDERED,
      this.onTrackChanged.bind(this),
    );

    this.dashJS.on(
      dashjs.MediaPlayer.events.TRACK_CHANGE_RENDERED,
      this.onTrackChanged.bind(this),
    );    

    this.dashJS.on(
      dashjs.MediaPlayer.events.ERROR,
      this.onDashJsError.bind(this),
    );
  }

  setCurrentQualityLevel(qualityLevelIndex: number, kind: QualityLevelKind = QualityLevelKind.VIDEO): QualityLevel {
    log.debug('Dash setCurrentQualityLevel', qualityLevelIndex);

    if (qualityLevelIndex !== -1) {
      this.dashJS.updateSettings({
        streaming: { abr: { autoSwitchBitrate: { [kind]: false } } },
      });
      this.dashJS.setQualityFor('video', qualityLevelIndex, true);
      this.qualityLevelCurrentIndex[kind] = this.dashJS.getQualityFor(kind);
    } 
    else {
      this.dashJS.updateSettings({
        streaming: { abr: { autoSwitchBitrate: { [kind]: true } } },
      });
    }

    return this.getCurrentQualityLevel(kind);
  }

  isAutoQualityLevel(kind: QualityLevelKind = QualityLevelKind.VIDEO): boolean {
    if(QualityLevelKind.AUDIO === kind)
      return this.dashJS.getSettings().streaming?.abr?.autoSwitchBitrate?.audio === true
    else
      return this.dashJS.getSettings().streaming?.abr?.autoSwitchBitrate?.video === true
  }

  isAutoQualityLevelAvailable(kind: QualityLevelKind): boolean {
    return true;
  }

  setCurrentAudioTrack(audioTrackIndex: number): AudioTrack {
    this.dashJS.setCurrentTrack(this.audioTracks[audioTrackIndex].data as any);
    this.currentAudioTrack = audioTrackIndex;

    this._events.audioTrackChanged(
      this.getCurrentAudioTrack(),
      audioTrackIndex,
    );

    return this.getCurrentAudioTrack();
  }

  setCurrenVideoTrack(videoTrackIndex: number): VideoTrack {
    this.dashJS.setCurrentTrack(this.videoTracks[videoTrackIndex].data as any);
    this.currentVideoTrack = videoTrackIndex;

    this._events.videoTrackChanged(
      this.getCurrentVideoTrack(),
      videoTrackIndex,
    );

    return this.getCurrentVideoTrack();
  }

  setCurrentTextTrack(index: number): TextTrack {
    if(this.isDashSubtitlesEnabled) {
      log.debug('Dash setCurrentTextTrack', index, this.textTracks, this.textTracks[index]);
      if(index === -1) {
        this.dashJS.enableText(false);        
      }
      else {
        this.dashJS.enableText(true);
        this.dashJS.setCurrentTrack(this.textTracks[index].data as any);
      }

      this.currentTextTrack = index;      

      this._events.textTrackChanged(this.getCurrentTextTrack(), index);

      return this.getCurrentTextTrack();
    }
    else {
      return super.setCurrentTextTrack(index);
    }
  }

  stop() {
    if (this.loadRetryer) {
      clearTimeout(this.loadRetryer);
      this.loadRetryer = null as any;
    }

    if (this.dashJS) {
      this.dashJS.reset();
    }
  }

  destroy() {
    super.destroy();
    
    if (this.loadRetryer) {
      clearTimeout(this.loadRetryer);
      this.loadRetryer = null as any;
    }

    if (this.dashJS) {
      this.dashJS.reset();
    }

    this.dashJS = null as any;
  }

  getLibrary(): dashjs.MediaPlayerClass {
    return this.dashJS;
  }
}

export { Dash };
